/**
 * Hub Users list view
 */

import React, { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { usersAPI } from '../../../utils/scheduler/api'
import { useToast } from '../../../contexts/ToastContext'
import ConfirmDialog from '../../scheduler/ConfirmDialog'
import {
  DatatableCard,
  DatatableEmpty,
  DatatableScroll,
  DatatableTh,
  DATATABLE_CELL_CLASS,
  DATATABLE_CELL_PRIMARY_CLASS,
  DATATABLE_TABLE_CLASS,
  DATATABLE_TBODY_CLASS,
  DATATABLE_THEAD_CLASS,
  useClientDatatable,
} from '../../shared/Datatable'
import DatatablePagination from '../../shared/DatatablePagination'
import { formatGroupLabel, formatUserGroupsLabel } from './shared.js'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'

export default function HubUsersList() {
  const navigate = useNavigate()
  const { success: toastSuccess, error: toastError } = useToast()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, user: null })
  const [searchText, setSearchText] = useState('')
  const [hideInactive, setHideInactive] = useState(true)

  const displayedUsers = useMemo(() => {
    if (!hideInactive) return users
    return users.filter((user) => user.active !== 0)
  }, [users, hideInactive])

  const usersTable = useClientDatatable(displayedUsers, {
    pageLength: 10,
    initialSort: { key: 'name', dir: 'asc' },
    searchText,
    filterItem: (user, q) => {
      const name = (user.name || '').toLowerCase()
      const email = (user.email || '').toLowerCase()
      const groupsLabel = formatUserGroupsLabel(user).toLowerCase()
      return name.includes(q) || email.includes(q) || groupsLabel.includes(q)
    },
    getSortValue: (user, key) => {
      if (key === 'groups') return formatUserGroupsLabel(user)
      if (key === 'status') return user.active === 0 ? 0 : 1
      return user[key]
    },
  })

  const loadUsers = async () => {
    try {
      setLoading(true)
      const data = await usersAPI.list()
      setUsers(data.users || [])
    } catch (error) {
      console.error('Error loading users:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadUsers()
  }, [])

  const handleDelete = async (id) => {
    try {
      await usersAPI.delete(id)
      await loadUsers()
      toastSuccess('User deleted.')
    } catch (error) {
      toastError(error.message || 'Failed to delete')
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="text-gray-600">Loading users...</div>
      </div>
    )
  }

  return (
    <div>
      <ConfirmDialog
        open={deleteConfirm.open}
        title="Delete user?"
        message="This will permanently remove this user."
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        onClose={() => setDeleteConfirm({ open: false, user: null })}
        onConfirm={async () => {
          const id = deleteConfirm.user?.id
          setDeleteConfirm({ open: false, user: null })
          if (id) await handleDelete(id)
        }}
      >
        {deleteConfirm.user ? (
          <div className="space-y-1">
            <div className="font-medium text-gray-900">{deleteConfirm.user.name || 'User'}</div>
            <div>
              <span className="text-gray-600">Email:</span>{' '}
              <span className="text-gray-900">{deleteConfirm.user.email || 'N/A'}</span>
            </div>
          </div>
        ) : null}
      </ConfirmDialog>

      <div className="mb-4 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-xl font-semibold">Manage Users</h2>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button asChild variant="outline" className="w-full sm:w-auto">
            <Link to="/hub/settings/users/invite">Invite User</Link>
          </Button>
          <Button asChild variant="success" className="w-full sm:w-auto">
            <Link to="/hub/settings/users/new">Add New User</Link>
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        <Input
          id="hub-users-search"
          type="search"
          placeholder="Search name, email, or group"
          className="min-w-[200px] max-w-sm"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <label className="inline-flex items-center gap-2 text-base text-foreground">
          <input
            type="checkbox"
            checked={hideInactive}
            onChange={(e) => setHideInactive(e.target.checked)}
            className="rounded border-input text-primary"
          />
          Hide inactive users
        </label>
      </div>

      <DatatableCard footer={<DatatablePagination {...usersTable} itemLabel="users" />}>
        <DatatableScroll>
          <table className={DATATABLE_TABLE_CLASS}>
            <thead className={DATATABLE_THEAD_CLASS}>
              <tr>
                <DatatableTh sortKey="name" sort={usersTable.sort} onSort={usersTable.toggleSort}>
                  Name
                </DatatableTh>
                <DatatableTh sortKey="email" sort={usersTable.sort} onSort={usersTable.toggleSort}>
                  Email
                </DatatableTh>
                <DatatableTh sortKey="groups" sort={usersTable.sort} onSort={usersTable.toggleSort}>
                  Groups
                </DatatableTh>
                <DatatableTh sortKey="status" sort={usersTable.sort} onSort={usersTable.toggleSort}>
                  Status
                </DatatableTh>
                <DatatableTh excludeFromOrdering align="right">
                  <span className="sr-only">Actions</span>
                </DatatableTh>
              </tr>
            </thead>
            <tbody className={DATATABLE_TBODY_CLASS}>
              {usersTable.sortedItems.length === 0 ? (
                <DatatableEmpty
                  colSpan={5}
                  message={
                    searchText
                      ? `No users found matching "${searchText}"`
                      : hideInactive
                        ? 'No active users found'
                        : 'No users found'
                  }
                />
              ) : (
                usersTable.rows.map((user) => (
                  <tr
                    key={user.id}
                    className="cursor-pointer hover:bg-gray-50/80"
                    onClick={() => navigate(`/hub/settings/users/${user.id}`)}
                  >
                    <td className={DATATABLE_CELL_PRIMARY_CLASS}>
                        <div className="flex items-center">
                          <div className="font-medium text-green-800">{user.name}</div>
                          {user.billing_enabled === 0 && (
                            <span className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                              Non-billable
                            </span>
                          )}
                        </div>
                      </td>
                      <td className={DATATABLE_CELL_CLASS}>
                        <div className="text-gray-600">{user.email}</div>
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          <span className="sr-only">{formatUserGroupsLabel(user)}</span>
                          {(user.group_names?.length
                            ? user.group_names
                            : [user.group_name || 'Unknown']
                          ).map((groupName) => (
                            <span
                              key={`${user.id}-${groupName}`}
                              className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800"
                            >
                              {formatGroupLabel(groupName)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className={DATATABLE_CELL_CLASS}>
                        {user.active === 0 ? (
                          <Badge variant="destructive">Inactive</Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                            Active
                          </Badge>
                        )}
                      </td>
                      <td className={`${DATATABLE_CELL_CLASS} text-end font-medium`}>
                        <div
                          className="flex items-center justify-end gap-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button asChild variant="ghost" size="sm" className="text-primary">
                            <Link to={`/hub/settings/users/${user.id}`}>Edit</Link>
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            onClick={() => setDeleteConfirm({ open: true, user })}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
        </DatatableScroll>
      </DatatableCard>
    </div>
  )
}
